import { useEffect, useRef } from 'react';

const DEFAULT_EVENTS = ['keydown', 'mousedown', 'scroll', 'touchstart'] as const;

type EventType = (typeof DEFAULT_EVENTS)[number];

interface UseInactivityWarningProps {
  totalTimeoutMs: number;
  warningMs: number;
  onWarning: () => void;
  onTimeout: () => void;
  events?: EventType[];
}

export function useInactivityWarning({
  totalTimeoutMs,
  warningMs,
  onWarning,
  onTimeout,
  events,
}: UseInactivityWarningProps): void {
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Store stable references to callbacks
  const warningCallbackRef = useRef(onWarning);
  const timeoutCallbackRef = useRef(onTimeout);

  useEffect(() => {
    warningCallbackRef.current = onWarning;
    timeoutCallbackRef.current = onTimeout;
  }, [onWarning, onTimeout]);

  useEffect(() => {
    const eventList = events ?? DEFAULT_EVENTS;

    const resetTimers = () => {
      lastActivityRef.current = Date.now();
      if (warningTimer.current) clearTimeout(warningTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);

      warningTimer.current = setTimeout(() => {
        warningCallbackRef.current();
        logoutTimer.current = setTimeout(() => {
          timeoutCallbackRef.current();
        }, warningMs);
      }, totalTimeoutMs - warningMs);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const now = Date.now();
        const inactiveMs = now - lastActivityRef.current;

        if (inactiveMs >= totalTimeoutMs) {
          timeoutCallbackRef.current();
        } else if (inactiveMs >= totalTimeoutMs - warningMs) {
          warningCallbackRef.current();
          // Set timeout for remaining warning period
          if (logoutTimer.current) clearTimeout(logoutTimer.current);
          logoutTimer.current = setTimeout(() => {
            timeoutCallbackRef.current();
          }, totalTimeoutMs - inactiveMs);
        } else {
          // Reset timers for remaining time
          resetTimers();
        }
      }
    };

    eventList.forEach((event) => window.addEventListener(event, resetTimers));
    document.addEventListener('visibilitychange', handleVisibilityChange);

    resetTimers();

    return () => {
      if (warningTimer.current) clearTimeout(warningTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      eventList.forEach((event) => window.removeEventListener(event, resetTimers));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [totalTimeoutMs, warningMs, events]);
}
